import { Box, Divider, Typography } from "@mui/material";
import { useState } from "react";
import { useSearchParams } from "react-router";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import { stringComparerBy } from "@entrilia/shared/utilities/arrayHelper";
import adminApi from "../../../api/adminApi";
import type { ObjectClassDefinitionInfo } from "../../../api/types/objectTypes";
import useClientContext from "../../../context/useClientContext";
import ObjectsListPage from "./ObjectsListPage";
import ObjectsPageNavigation from "./ObjectsPageNavigation";

const RecordsPage = () => {
  const { hasAnyPermission } = useClientContext();

  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedObjectType, setSelectedObjectType] = useState<string | undefined>(
    searchParams.get("type") ?? undefined
  );

  const [availableCustomObjectDefinitions, setAvailableCustomObjectDefinitions] = useState<ObjectClassDefinitionInfo[]>(
    []
  );

  const [objectDefinitions, fetchError, { isFetching }] = useFetch(adminApi.getAvailableObjectDefinitions, {
    onSetData: (data) => {
      const availableCustomObjectDefinitions = data
        .filter(
          ({ objectClassDefinition, objectPermissions }) =>
            objectClassDefinition.source === "Custom" && hasAnyPermission(objectPermissions.objectDataReadPermissions)
        )
        .map(({ objectClassDefinition }) => objectClassDefinition)
        .sort(stringComparerBy((d) => d.objectName));

      setAvailableCustomObjectDefinitions(availableCustomObjectDefinitions);

      if (
        !selectedObjectType ||
        !availableCustomObjectDefinitions.some((def) => def.objectType === selectedObjectType)
      ) {
        setSelectedObjectType(availableCustomObjectDefinitions[0]?.objectType);
      }
    },
  });

  if (fetchError) {
    logError(fetchError, "[ProductObjectsPage]");
    return <DataLoadingFailed title="Loading objects definitions failed" />;
  }

  if (isFetching || objectDefinitions === undefined) {
    return <InlineLoader />;
  }

  const handleObjectTypeChange = (objectType: string) => {
    setSelectedObjectType(objectType);
    setSearchParams({ type: objectType });
  };

  if (availableCustomObjectDefinitions.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", pt: 2.5 }}>
        <Typography color="textSecondary">No records available for this client.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", overflow: "hidden", height: "100%" }}>
      <ObjectsPageNavigation
        selectedObjectType={selectedObjectType}
        onSelectedObjectTypeChange={handleObjectTypeChange}
        objectDefinitions={availableCustomObjectDefinitions}
      />
      <Divider flexItem orientation="vertical" />
      {selectedObjectType && (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%", flex: 1 }}>
          <ObjectsListPage
            key={selectedObjectType}
            objectType={selectedObjectType}
            objectDetailsPathPattern=":type/:id"
            allowCreate
          />
        </Box>
      )}
    </Box>
  );
};

export default RecordsPage;
